import React, { useState } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { useMeterReadingStatusList } from "@features/mx/settings/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Eye, Pencil, Trash, Plus } from "lucide-react";
import { Button } from "@shared/ui/button";
import { useNavigate } from "react-router-dom";
import { AddConditionStatusModal } from "../create/AddConditionStatus";
import { PageHeader } from "@shared/ui/pageHeader";
import { PAGE_SIZE } from "@shared/utils/constants";

export function ConditionStatusTab() {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  const { data: response, isLoading } = useMeterReadingStatusList(
    remoteUtilityId,
    currentPage,
    PAGE_SIZE,
    searchQuery
  );

  const tableData = React.useMemo(() => {
    if (!response?.results || !Array.isArray(response.results)) {
      return [];
    }
    return response.results.map((s: any) => ({
      ...s,
      id: s.id.toString(),
      name: s.name || s.statusName || "—",
      systemStatus: s.codeStatus,
      codeId: s.codeId,
      created: s.createdDate ? String(s.createdDate) : "—",
      createdBy: s.createdUserRemoteName || s.createdBy || "—",
    }));
  }, [response]);

  const totalItems = response?.count || 0;

  const pagination = {
    currentPage,
    totalPages: Math.ceil(totalItems / PAGE_SIZE) || 1,
    totalItems,
    itemsPerPage: PAGE_SIZE,
    startItem: totalItems === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1,
    endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
  };

  const columns: TableColumn<any>[] = [
    { key: "name", header: "Status Name" },
    {
      key: "systemStatus",
      header: "System Status",
      render: (val: string) => (
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${val === "Active"
          ? "bg-green-50 text-green-700 border-green-200"
          : "bg-gray-50 text-gray-600 border-gray-200"
          }`}>
          {val}
        </span>
      ),
    },
    { key: "created", header: "Created On", },
    { key: "createdBy", header: "Created By" },
  ];

  const actions = [
    {
      label: "Edit",
      icon: <Pencil className="w-4 h-4 text-gray-700" />,
      onClick: (row: any) => {
        setSelectedStatus(row);
        setIsAddOpen(true);
      },
    },
    {
      label: "Delete",
      icon: <Trash className="w-4 h-4 text-red-500" />,
      onClick: (row: any) => console.log("Delete", row),
      disabled: () => true
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <PageHeader
          title="Condition Statuses"
          description="Manage and configure condition status types"
        />
        <Button
          onClick={() => {
            setSelectedStatus(null);
            setIsAddOpen(true);
          }}
          className="bg-primary text-white"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Conditional Status
        </Button>
      </div>
      <div className="border border-gray-150 rounded-xl overflow-hidden p-4">
        <GenericTable
          columns={columns}
          data={tableData}
          rowKey="id"
          loading={isLoading}
          actions={actions}
          actionsType="icons"
          searchPlaceholder="Search condition statuses..."
          onSearch={(val) => {
            setSearchQuery(val);
            setCurrentPage(1);
          }}
          pagination={pagination}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>

      <AddConditionStatusModal
        open={isAddOpen}
        onOpenChange={(open) => {
          setIsAddOpen(open);
          if (!open) setSelectedStatus(null);
        }}
        editData={selectedStatus}
      />
    </div>
  );
}

export default ConditionStatusTab;
